/**
 * YouTube Player Utility
 * Provides utilities for playing YouTube videos using the YouTube IFrame API.
 * Uses global state on window to support multiple webpack bundles.
 */

const YT_API_URL = 'https://www.youtube.com/iframe_api';
const YT_API_SCRIPT_ID = 'youtube-iframe-api';
const TIMEOUT_MS = 10000;

// Use global state to share between bundles
const GLOBAL_STATE_KEY = '__papaLpYouTubeAPI';

/**
 * Get or initialize global API state
 */
function getGlobalState() {
  if (!window[GLOBAL_STATE_KEY]) {
    window[GLOBAL_STATE_KEY] = {
      ready: false,
      loading: false,
      error: null,
      pendingCallbacks: [],
      players: new Map(),
    };
  }
  return window[GLOBAL_STATE_KEY];
}

/**
 * Extract video ID from YouTube URL
 * @param {string} url - YouTube video URL or ID
 * @returns {string|null} Video ID or null if invalid
 */
export function extractVideoId(url) {
  if (!url || typeof url !== 'string') return null;
  
  // Direct video ID (11 characters)
  if (/^[a-zA-Z0-9_-]{11}$/.test(url)) return url;
  
  // Extract from various YouTube URL formats
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
  return match ? match[1] : null;
}

/**
 * Load YouTube IFrame API script
 * @returns {Promise<void>}
 */
function loadYouTubeAPI() {
  return new Promise((resolve, reject) => {
    const state = getGlobalState();

    // Already ready (check window.YT as primary indicator)
    if (window.YT?.Player) {
      state.ready = true;
      state.loading = false;
      return resolve();
    }
    
    // Previous load failed
    if (state.error) {
      return reject(state.error);
    }
    
    // Currently loading - queue callback
    if (state.loading) {
      return state.pendingCallbacks.push({ resolve, reject });
    }
    
    // Check for existing script (might be loaded by another bundle)
    if (document.getElementById(YT_API_SCRIPT_ID)) {
      state.loading = true;
      state.pendingCallbacks.push({ resolve, reject });
      return waitForAPI();
    }
    
    // Start loading
    state.loading = true;
    state.pendingCallbacks.push({ resolve, reject });
    
    // Global callback for API ready
    const originalCallback = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      const s = getGlobalState();
      s.ready = true;
      s.loading = false;
      flushCallbacks();
      // Call original callback if exists (from other code)
      if (originalCallback) originalCallback();
    };
    
    // Inject script
    const script = document.createElement('script');
    script.id = YT_API_SCRIPT_ID;
    script.src = YT_API_URL;
    script.async = true;
    script.onerror = () => handleAPIError(new Error('Failed to load YouTube IFrame API'));
    
    document.head.appendChild(script);
    
    // Timeout fallback
    setTimeout(() => {
      const s = getGlobalState();
      if (!s.ready && !window.YT?.Player) {
        handleAPIError(new Error('YouTube API load timeout'));
      }
    }, TIMEOUT_MS);
  });
}

/**
 * Wait for API to be ready (when script already exists)
 */
function waitForAPI() {
  const startTime = Date.now();
  const checkInterval = setInterval(() => {
    const state = getGlobalState();
    // Check window.YT as primary indicator
    if (window.YT?.Player) {
      clearInterval(checkInterval);
      state.ready = true;
      state.loading = false;
      flushCallbacks();
    } else if (Date.now() - startTime > TIMEOUT_MS) {
      clearInterval(checkInterval);
      handleAPIError(new Error('YouTube API ready timeout'));
    }
  }, 100);
}

/**
 * Handle API load error
 */
function handleAPIError(error) {
  const state = getGlobalState();
  state.loading = false;
  state.error = error;
  state.pendingCallbacks.forEach(({ reject }) => reject(error));
  state.pendingCallbacks.length = 0;
}

/**
 * Flush all pending callbacks
 */
function flushCallbacks() {
  const state = getGlobalState();
  state.pendingCallbacks.forEach(({ resolve }) => resolve());
  state.pendingCallbacks.length = 0;
}

/**
 * Create control methods for a player
 */
function createPlayerControls(player, playerId, videoId) {
  const safeCall = (fn, fallback) => {
    try {
      return fn();
    } catch (error) {
      console.error('YouTube player error:', error);
      return fallback;
    }
  };
  
  return {
    player,
    playerId,
    videoId,
    provider: 'youtube',
    play: () => safeCall(() => player.playVideo()),
    pause: () => safeCall(() => player.pauseVideo()),
    mute: () => safeCall(() => player.mute()),
    unmute: () => safeCall(() => player.unMute()),
    toggleMute: () => safeCall(() => player.isMuted() ? player.unMute() : player.mute()),
    isMuted: () => safeCall(() => player.isMuted(), false),
    isPlaying: () => safeCall(() => player.getPlayerState() === 1, false), // YT.PlayerState.PLAYING = 1
    getState: () => safeCall(() => player.getPlayerState(), -1),
    destroy: () => {
      safeCall(() => player.destroy());
      getGlobalState().players.delete(playerId);
    }
  };
}

/**
 * Wait for player to be ready
 */
function waitForPlayer(player, playerId) {
  return new Promise((resolve, reject) => {
    const startTime = Date.now();
    const checkInterval = setInterval(() => {
      if (typeof player.playVideo === 'function') {
        clearInterval(checkInterval);
        resolve(player);
      } else if (Date.now() - startTime > TIMEOUT_MS) {
        clearInterval(checkInterval);
        reject(new Error('Player initialization timeout'));
      }
    }, 100);
  });
}

/**
 * Get container element
 */
function getContainer(container) {
  if (typeof container === 'string') {
    return document.getElementById(container) || document.querySelector(container);
  }
  return container;
}

/**
 * Create a YouTube player instance
 * @param {string|HTMLElement} container - Container element or selector
 * @param {string} videoUrl - YouTube video URL or ID
 * @param {object} options - Player options
 * @returns {Promise<object>} Player controls
 */
export async function createYouTubePlayer(container, videoUrl, options = {}) {
  await loadYouTubeAPI();
  
  const videoId = extractVideoId(videoUrl);
  if (!videoId) {
    throw new Error(`Invalid YouTube URL: ${videoUrl}`);
  }
  
  const containerElement = getContainer(container);
  if (!containerElement) {
    throw new Error('Container element not found');
  }
  
  // Generate unique player ID
  const playerId = `yt-player-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  containerElement.id = containerElement.id || playerId;
  
  // Build player configuration
  const config = {
    videoId,
    width: options.width || '100%',
    height: options.height || '100%',
    playerVars: {
      autoplay: options.autoplay ? 1 : 0,
      mute: options.mute ? 1 : 0,
      controls: options.controls !== false ? 1 : 0,
      rel: options.showRelatedVideos !== false ? 1 : 0,
      modestbranding: options.modestBranding ? 1 : 0,
      loop: options.loop ? 1 : 0,
      playlist: options.loop ? videoId : undefined, // Required for loop to work
      ...options.playerVars
    },
    events: {
      onReady: options.onReady,
      onStateChange: options.onStateChange,
      onError: options.onError
    }
  };
  
  // Create player
  const player = new window.YT.Player(containerElement.id, config);
  
  // Wait for player to be ready
  await waitForPlayer(player, playerId);
  
  // Store and return controls
  const state = getGlobalState();
  state.players.set(playerId, player);
  return createPlayerControls(player, playerId, videoId);
}

/**
 * Get a player instance by ID
 * @param {string} playerId - Player ID
 * @returns {object|null} Player controls or null
 */
export function getPlayer(playerId) {
  const state = getGlobalState();
  const player = state.players.get(playerId);
  return player ? createPlayerControls(player, playerId) : null;
}

/**
 * Check if YouTube API is ready
 * @returns {boolean}
 */
export function isAPIReady() {
  return window.YT?.Player !== undefined;
}

/**
 * Destroy all players
 */
export function destroyAllPlayers() {
  const state = getGlobalState();
  state.players.forEach((player, id) => {
    try {
      player.destroy();
    } catch (error) {
      console.error(`Error destroying player ${id}:`, error);
    }
  });
  state.players.clear();
}
